import type { LocalizedLabel } from './localized';

/**
 * What every onboarding import problem means and how to fix it, in both languages (Release 1).
 *
 * One catalogue for the import centre and the downloadable error report, so a person reads the same
 * explanation on screen and in the file. A code missing here is shown as the code itself; the unit
 * test pins that every code an onboarding importer can raise is described.
 */
export interface ImportIssueText {
  message: LocalizedLabel;
  fix: LocalizedLabel;
}

const t = (ar: string, en: string, fixAr: string, fixEn: string): ImportIssueText => ({
  message: { ar, en },
  fix: { ar: fixAr, en: fixEn },
});

export const IMPORT_ISSUE_TEXT: Readonly<Record<string, ImportIssueText>> = {
  /* ------------------------------------------- campaign performance (ADR-0042) */
  UNKNOWN_CAMPAIGN: t(
    'لا توجد حملة بهذا الاسم ضمن صلاحياتك.',
    'No campaign with this name is within your access.',
    'اكتب اسم الحملة تمامًا كما هو مسجّل، أو أنشئ الحملة أولًا.',
    'Write the campaign name exactly as recorded, or create the campaign first.',
  ),
  CAMPAIGN_AMBIGUOUS: t(
    'يوجد أكثر من حملة بهذا الاسم في شركات مختلفة.',
    'More than one campaign has this name, in different companies.',
    'استخدم معرّف الحملة بدلًا من اسمها.',
    'Use the campaign’s identifier instead of its name.',
  ),
  CAMPAIGN_ARCHIVED: t(
    'الحملة مؤرشفة؛ لا تُضاف إليها أرقام.',
    'The campaign is archived; no figures can be added.',
    'أعد الحملة إلى مسودة أولًا إن كان ذلك مقصودًا.',
    'Return the campaign to draft first if this is intended.',
  ),
  PERFORMANCE_KIND_EXPECTED: t(
    'نوع الأرقام يجب أن يكون daily أو cumulative.',
    'The kind must be daily or cumulative.',
    'daily = أرقام ذلك اليوم وحده؛ cumulative = الإجمالي حتى ذلك اليوم.',
    'daily = that day’s figures alone; cumulative = the total up to that day.',
  ),
  COUNT_EXPECTED: t(
    'القيمة يجب أن تكون عددًا صحيحًا دون فواصل أو رموز.',
    'The value must be a whole number without separators or symbols.',
    'اكتب 12500 وليس 12,500 أو 1.2e4.',
    'Write 12500, not 12,500 or 1.2e4.',
  ),
  PERFORMANCE_ALREADY_RECORDED: t(
    'سُجّلت أرقام مختلفة لهذه الحملة في هذا التاريخ من قبل.',
    'Different figures were already recorded for this campaign on this date.',
    'غيّر الرقم المسجّل من صفحة الحملة بـ«تصحيح» مع ذكر السبب؛ الاستيراد لا يستبدل رقمًا مسجّلًا.',
    'Change the recorded figure on the campaign page with “Correct” and a reason; an import never replaces a recorded figure.',
  ),
  PERFORMANCE_MODE_MISMATCH: t(
    'هذه الحملة تُتابَع بنوع أرقام آخر (يومي أو تراكمي).',
    'This campaign is tracked with the other kind of figures (daily or cumulative).',
    'استخدم نوع الأرقام نفسه لكل صفوف الحملة؛ لا يُخلط اليومي بالتراكمي.',
    'Use the same kind for every row of the campaign; daily and cumulative are never mixed.',
  ),
  PERFORMANCE_DATE_IN_FUTURE: t(
    'التاريخ في المستقبل.',
    'The date is in the future.',
    'الأرقام تُسجَّل لأيام مضت فقط.',
    'Figures are recorded for past days only.',
  ),
  PERFORMANCE_BEFORE_START: t(
    'التاريخ قبل بداية الحملة.',
    'The date is before the campaign starts.',
    'صحّح التاريخ، أو عدّل تاريخ بداية الحملة إن كان خاطئًا.',
    'Correct the date, or the campaign’s start date if that is wrong.',
  ),
  /* ------------------------------------------------------ attendance (HR-TIME-001) */
  UNKNOWN_EMPLOYEE: t(
    'لا يوجد موظف بهذا الرقم ضمن صلاحياتك.',
    'No employee with this number is within your access.',
    'اكتب رقم الموظف كما في ملفه.',
    'Write the employee number exactly as on their record.',
  ),
  EMPLOYEE_AMBIGUOUS: t(
    'يوجد أكثر من موظف بهذا الرقم في شركات مختلفة.',
    'More than one employee has this number, in different companies.',
    'استورد ملف كل شركة على حدة.',
    'Import each company’s file separately.',
  ),
  TIME_REQUIRED: t(
    'لا يوجد وقت حضور ولا انصراف.',
    'Neither a check-in nor a check-out time is given.',
    'اكتب وقتًا واحدًا على الأقل بصيغة ساعة:دقيقة (مثل 08:30).',
    'Write at least one time as hour:minute (e.g. 08:30).',
  ),
  CHECK_OUT_BEFORE_CHECK_IN: t(
    'وقت الانصراف ليس بعد وقت الحضور.',
    'The check-out time is not after the check-in time.',
    'صحّح الوقتين؛ الوردية الممتدة بعد منتصف الليل تُسجّل على يومين.',
    'Correct the times; a shift past midnight is recorded on two days.',
  ),
  ATTENDANCE_EXISTS: t(
    'يوجد سجل حضور مختلف لهذا الموظف في هذا اليوم.',
    'A different attendance record already exists for this employee on this day.',
    'صحّح السجل الموجود من صفحة الموظف مع ذكر السبب، أو احذف الصف من الملف.',
    'Correct the stored record on the employee’s page with a reason, or remove the row from the file.',
  ),
  BEFORE_HIRE: t(
    'التاريخ قبل تاريخ تعيين الموظف.',
    'The date is before the employee’s hire date.',
    'صحّح التاريخ.',
    'Correct the date.',
  ),
  PERFORMANCE_FIGURES_INCONSISTENT: t(
    'الوصول أكبر من مرات الظهور.',
    'Reach is greater than impressions.',
    'الوصول (أشخاص فريدون) لا يتجاوز مرات الظهور؛ راجع الأعمدة.',
    'Reach (unique people) cannot exceed impressions; check the columns.',
  ),
  /* ---------------------------------------------------------------- the file */
  FILE_EMPTY: t(
    'الملف فارغ.',
    'The file is empty.',
    'اختر الملف الصحيح.',
    'Choose the right file.',
  ),
  FILE_TOO_LARGE: t(
    'الملف أكبر من 5 ميجابايت.',
    'The file is larger than 5 MB.',
    'قسّم البيانات على أكثر من ملف.',
    'Split the data into several files.',
  ),
  TOO_MANY_ROWS: t(
    'الملف يحتوي على أكثر من 5000 صف.',
    'The file has more than 5,000 rows.',
    'قسّم البيانات على أكثر من ملف.',
    'Split the data into several files.',
  ),
  XLSX_UNREADABLE: t(
    'تعذرت قراءة ملف Excel.',
    'The Excel file could not be read.',
    'احفظه من جديد بصيغة .xlsx أو CSV UTF-8.',
    'Save it again as .xlsx or CSV UTF-8.',
  ),
  XLSX_TOO_COMPLEX: t(
    'ملف Excel كبير جدًا بعد فك الضغط.',
    'The Excel file is too large once unpacked.',
    'احذف الأوراق والصور غير اللازمة أو صدّره CSV.',
    'Remove extra sheets and images, or export it as CSV.',
  ),
  CSV_NOT_UTF8: t(
    'ملف CSV ليس بترميز UTF-8.',
    'The CSV file is not UTF-8.',
    'من Excel اختر «CSV UTF-8» عند الحفظ.',
    'In Excel, choose “CSV UTF-8” when saving.',
  ),
  CSV_MALFORMED: t(
    'ملف CSV به علامات اقتباس غير مغلقة.',
    'The CSV file has an unclosed quote.',
    'افتحه في Excel واحفظه من جديد.',
    'Open it in Excel and save it again.',
  ),
  FILE_TYPE_NOT_ALLOWED: t(
    'نوع الملف غير مدعوم.',
    'This file type is not supported.',
    'استخدم .xlsx أو CSV UTF-8.',
    'Use .xlsx or CSV UTF-8.',
  ),
  NO_ROWS: t(
    'لا توجد صفوف بيانات تحت العناوين.',
    'There are no data rows under the header.',
    'أضف البيانات في الورقة الأولى تحت صف العناوين.',
    'Add data on the first sheet under the header row.',
  ),
  MISSING_COLUMN: t(
    'عمود مطلوب غير مربوط بأي عنوان في الملف.',
    'A required column is not mapped to any header in the file.',
    'اربط العمود بعنوان الملف المناسب أو أضفه للملف.',
    'Map the column to the matching file header, or add it to the file.',
  ),
  DUPLICATE_MAPPING: t(
    'نفس عنوان الملف مربوط بعمودين.',
    'The same file header is mapped to two columns.',
    'اختر عنوانًا مختلفًا لكل عمود.',
    'Choose a different header for each column.',
  ),
  UNKNOWN_COLUMN: t(
    'عنوان عمود غير معروف.',
    'Unknown column header.',
    'استخدم عناوين القالب كما هي.',
    'Use the template’s headers as they are.',
  ),
  /* ---------------------------------------------------------------- cells */
  REQUIRED: t(
    'قيمة مطلوبة فارغة.',
    'A required value is empty.',
    'املأ الخلية.',
    'Fill in the cell.',
  ),
  ONE_OF_REQUIRED: t(
    'يجب ملء أحد العمودين البديلين.',
    'One of the alternative columns must be filled.',
    'املأ معرّف المصدر أو البديل (مثل الهاتف أو رقم العقد).',
    'Fill the source ID or its alternative (such as the phone or contract number).',
  ),
  ONE_OF_CONFLICT: t(
    'تم ملء العمودين البديلين معًا.',
    'Both alternative columns are filled.',
    'اترك واحدًا منهما فقط.',
    'Keep only one of them.',
  ),
  CELL_STORED_AS_NUMBER: t(
    'الخلية محفوظة كرقم في Excel وقد ضاع الصفر في بدايتها.',
    'Excel stored the cell as a number, so a leading zero is lost.',
    'اجعل تنسيق العمود «نص» ثم اكتب القيمة من جديد.',
    'Format the column as Text, then type the value again.',
  ),
  DATE_FORMAT: t(
    'التاريخ ليس بصيغة YYYY-MM-DD.',
    'The date is not written as YYYY-MM-DD.',
    'اكتب التاريخ مثل 2025-03-31.',
    'Write the date like 2025-03-31.',
  ),
  DATE_INVALID: t(
    'تاريخ غير موجود.',
    'This date does not exist.',
    'صحح اليوم أو الشهر.',
    'Correct the day or month.',
  ),
  AMOUNT_FORMAT: t(
    'المبلغ ليس رقمًا عشريًا بسيطًا.',
    'The amount is not a plain decimal number.',
    'احذف فواصل الآلاف ورمز العملة، مثل 1500000.00.',
    'Remove thousands separators and currency symbols, e.g. 1500000.00.',
  ),
  AMOUNT_PRECISION: t(
    'المبلغ به منازل عشرية أكثر من المسموح.',
    'The amount has more decimals than allowed.',
    'استخدم منزلتين عشريتين على الأكثر (وأربعة للمساحة).',
    'Use at most two decimals (four for areas).',
  ),
  AMOUNT_NOT_POSITIVE: t(
    'المبلغ يجب أن يكون أكبر من صفر.',
    'The amount must be greater than zero.',
    'صحح المبلغ.',
    'Correct the amount.',
  ),
  AMOUNT_TOO_LARGE: t(
    'المبلغ كبير جدًا.',
    'The amount is too large.',
    'راجع المبلغ.',
    'Check the amount.',
  ),
  INTEGER_EXPECTED: t(
    'مطلوب رقم صحيح.',
    'A whole number is expected.',
    'اكتب رقمًا بدون كسور.',
    'Write a number without decimals.',
  ),
  OUT_OF_RANGE: t(
    'الرقم خارج الحدود المسموحة.',
    'The number is outside the allowed range.',
    'راجع التعليمات.',
    'Check the instructions.',
  ),
  SOURCE_ID_FORMAT: t(
    'معرّف المصدر غير صالح.',
    'The source ID is not valid.',
    'استخدم حروفًا إنجليزية وأرقامًا و . _ / - فقط، حتى 80 حرفًا.',
    'Use English letters, digits and . _ / - only, up to 80 characters.',
  ),
  EXAMPLE_ROW: t(
    'هذا صف من الأمثلة التوضيحية وليس بيانات حقيقية.',
    'This is a fictional example row, not real data.',
    'احذف صفوف الأمثلة (التي تبدأ بـ EXAMPLE-).',
    'Delete the example rows (those starting with EXAMPLE-).',
  ),
  CODE_FORMAT: t(
    'الكود غير صالح.',
    'The code is not valid.',
    'حروف إنجليزية كبيرة وأرقام و - و / فقط.',
    'Upper-case English letters, digits, - and / only.',
  ),
  PHONE_EXPECTED: t(
    'رقم الهاتف غير صالح.',
    'The phone number is not valid.',
    'من 6 إلى 15 رقمًا.',
    'Between 6 and 15 digits.',
  ),
  IDENTITY_NUMBER_EXPECTED: t(
    'رقم الهوية غير صالح.',
    'The identity number is not valid.',
    'حروف وأرقام ومسافات وشرطات فقط، من 3 إلى 40.',
    'Letters, digits, spaces and hyphens, 3 to 40 characters.',
  ),
  EMAIL_EXPECTED: t(
    'البريد الإلكتروني غير صالح.',
    'The e-mail address is not valid.',
    'صحح العنوان.',
    'Correct the address.',
  ),
  VALUE_NOT_ALLOWED: t(
    'القيمة ليست من القيم المسموحة.',
    'The value is not one of the allowed values.',
    'انسخ إحدى القيم من ورقة التعليمات كما هي.',
    'Copy one of the values from the instructions sheet exactly.',
  ),
  CURRENCY_CODE_EXPECTED: t(
    'رمز العملة غير صالح.',
    'The currency code is not valid.',
    'ثلاثة حروف كبيرة مثل EGP.',
    'Three capital letters such as EGP.',
  ),
  TOO_LONG: t(
    'النص أطول من المسموح.',
    'The text is longer than allowed.',
    'اختصر النص.',
    'Shorten the text.',
  ),
  TOO_SHORT: t(
    'النص أقصر من المسموح.',
    'The text is shorter than allowed.',
    'أكمل النص.',
    'Complete the text.',
  ),
  /* ---------------------------------------------------------------- across rows */
  DUPLICATE_SOURCE_ID: t(
    'نفس معرّف المصدر مكرر في الملف.',
    'The same source ID appears twice in the file.',
    'اجعل لكل سجل معرّفًا مختلفًا.',
    'Give every record its own identifier.',
  ),
  ALREADY_IMPORTED: t(
    'سبق استيراد هذا السجل؛ سيتم تخطيه.',
    'Already imported; it will be skipped.',
    'لا حاجة لأي إجراء.',
    'No action needed.',
  ),
  OUTSIDE_SCOPE: t(
    'السجل خارج نطاق صلاحياتك.',
    'The record is outside your access scope.',
    'اطلب من مسؤول الصلاحيات أو استورده بحساب آخر.',
    'Ask an administrator, or import it with an account that covers it.',
  ),
  UNKNOWN_BRANCH: t(
    'الفرع غير موجود.',
    'No branch has this code.',
    'استخدم كود فرع من الهيكل التنظيمي.',
    'Use a branch code from the organization.',
  ),
  DUPLICATE_IN_FILE: t(
    'نفس رقم الهاتف مكرر في الملف لنفس الكيان.',
    'The same phone appears twice in the file for one legal entity.',
    'ادمج الصفين في عميل واحد.',
    'Merge the two rows into one customer.',
  ),
  DUPLICATE_CUSTOMER_PHONE: t(
    'يوجد عميل بنفس رقم الهاتف في هذا الكيان.',
    'A customer with this phone already exists in this legal entity.',
    'احذف الصف، واربط العقود بهذا العميل عبر customer_phone.',
    'Remove the row, and link contracts to that customer with customer_phone.',
  ),
  EMAIL_ALREADY_USED: t(
    'البريد مستخدم لعميل آخر (تنبيه فقط).',
    'Another customer uses this e-mail (warning only).',
    'تأكد أنهما شخصان مختلفان.',
    'Make sure they are different people.',
  ),
  IDENTITY_INCOMPLETE: t(
    'نوع الهوية ورقمها يُملآن معًا.',
    'Identity type and number go together.',
    'املأ الاثنين أو اتركهما فارغين.',
    'Fill both, or leave both empty.',
  ),
  IDENTITY_NOT_PERMITTED: t(
    'ليس لديك صلاحية تسجيل أرقام الهوية.',
    'You may not record identity numbers.',
    'احذف أعمدة الهوية أو اطلب الصلاحية.',
    'Remove the identity columns, or ask for the permission.',
  ),
  OWNER_UNKNOWN: t(
    'لا يوجد حساب نشط بهذا البريد.',
    'No active account has this sign-in e-mail.',
    'صحح البريد أو اتركه فارغًا.',
    'Correct it, or leave it empty.',
  ),
  OWNER_NOT_PERMITTED: t(
    'تحديد مسؤول آخر للعميل يتطلب صلاحية نقل العملاء.',
    'Naming another owner needs the customer transfer permission.',
    'اترك العمود فارغًا أو اطلب الصلاحية.',
    'Leave it empty, or ask for the permission.',
  ),
  UNKNOWN_PROJECT: t(
    'المشروع غير موجود أو خارج نطاقك.',
    'No project with this code in your scope.',
    'أنشئ المشروع أولًا من شاشة المشروعات.',
    'Create the project first on the Projects screen.',
  ),
  UNKNOWN_BUILDING: t(
    'المبنى غير موجود في المشروع.',
    'No building with this code in the project.',
    'أنشئ المبنى أولًا من شاشة المشروع.',
    'Create the building first on the project screen.',
  ),
  UNIT_CODE_EXISTS: t(
    'توجد وحدة بنفس الكود في المشروع.',
    'A unit with this code already exists in the project.',
    'احذف الصف أو استخدم كودًا آخر.',
    'Remove the row, or use another code.',
  ),
  DUPLICATE_UNIT_IN_FILE: t(
    'نفس الوحدة مكررة في الملف.',
    'The same unit appears twice in the file.',
    'احذف التكرار.',
    'Remove the duplicate.',
  ),
  FLOOR_ABOVE_BUILDING: t(
    'الدور أعلى من عدد أدوار المبنى.',
    'The floor is above the building’s floors.',
    'صحح الدور أو بيانات المبنى.',
    'Correct the floor or the building.',
  ),
  CURRENCY_MISMATCH: t(
    'العملة تختلف عن عملة المشروع.',
    'The currency differs from the project’s.',
    'استخدم عملة المشروع.',
    'Use the project currency.',
  ),
  VIEW_INCOMPLETE: t(
    'الإطلالة تُكتب بالعربية والإنجليزية معًا.',
    'The view needs both Arabic and English.',
    'املأ الاثنين أو اتركهما.',
    'Fill both, or neither.',
  ),
  CUTOVER_NOT_CONFIGURED: t(
    'تاريخ بدء التشغيل (cutover) غير محدد.',
    'The cutover date is not set.',
    'يحدده مدير النظام في مركز الاستيراد.',
    'An administrator sets it in the import centre.',
  ),
  CONTRACT_AFTER_CUTOVER: t(
    'تاريخ العقد ليس قبل تاريخ بدء التشغيل.',
    'The contract date is not before the cutover date.',
    'العقود الجديدة تُسجل من الحجز والعقد بالطريقة العادية.',
    'New contracts go through a reservation and contract as usual.',
  ),
  CUSTOMER_NOT_FOUND: t(
    'العميل غير موجود أو خارج نطاقك.',
    'No such customer in your scope.',
    'استورد العملاء أولًا أو صحح المرجع.',
    'Import customers first, or correct the reference.',
  ),
  CUSTOMER_AMBIGUOUS: t(
    'المرجع يطابق أكثر من عميل.',
    'The reference matches more than one customer.',
    'استخدم customer_source_id.',
    'Use customer_source_id.',
  ),
  CUSTOMER_OTHER_ENTITY: t(
    'العميل في كيان قانوني مختلف عن الوحدة.',
    'The customer belongs to a different legal entity than the unit.',
    'راجع العميل أو الوحدة.',
    'Check the customer or the unit.',
  ),
  UNKNOWN_UNIT: t(
    'الوحدة غير موجودة في المشروع أو خارج نطاقك.',
    'No such unit in the project within your scope.',
    'استورد الوحدات أولًا أو صحح الكود.',
    'Import units first, or correct the code.',
  ),
  UNIT_NOT_AVAILABLE: t(
    'الوحدة ليست متاحة (محجوزة أو متعاقد عليها أو موقوفة).',
    'The unit is not available (held, reserved, contracted or withdrawn).',
    'راجع حالة الوحدة؛ لا يمكن بيع وحدة مرتين.',
    'Check the unit’s status; a unit cannot be sold twice.',
  ),
  UNIT_ALREADY_STAGED: t(
    'عقد آخر بانتظار جدوله يستخدم نفس الوحدة.',
    'Another contract awaiting its schedule uses this unit.',
    'راجع ملفات العقود.',
    'Check the contracts files.',
  ),
  SALES_OWNER_UNKNOWN: t(
    'لا يوجد حساب نشط بهذا البريد.',
    'No active account has this sign-in e-mail.',
    'صحح البريد أو اتركه فارغًا.',
    'Correct it, or leave it empty.',
  ),
  CONTRACT_NOT_STAGED: t(
    'لا يوجد عقد بانتظار جدوله بهذا المعرّف.',
    'No contract awaiting its schedule has this identifier.',
    'اعتمد ملف العقود أولًا بنفس مصدر البيانات.',
    'Commit the contracts file first, with the same source namespace.',
  ),
  CONTRACT_AMBIGUOUS: t(
    'المعرّف يطابق عقودًا في أكثر من كيان.',
    'The identifier matches contracts in several legal entities.',
    'استخدم contract_number.',
    'Use contract_number.',
  ),
  SEQUENCE_DUPLICATE: t(
    'رقم القسط مكرر لنفس العقد.',
    'The sequence repeats within the contract.',
    'رقّم الأقساط 1، 2، 3 ...',
    'Number the rows 1, 2, 3 …',
  ),
  SEQUENCE_GAP: t(
    'أرقام الأقساط غير متتالية من 1.',
    'Sequences are not consecutive from 1.',
    'رقّم الأقساط 1، 2، 3 بدون فجوات.',
    'Number the rows 1, 2, 3 with no gaps.',
  ),
  SCHEDULE_TOTAL_MISMATCH: t(
    'مجموع الأقساط لا يساوي قيمة العقد (السعر + وديعة الصيانة).',
    'The rows do not add up to the contract value (price + maintenance deposit).',
    'صحح المبالغ حتى يتطابق المجموع تمامًا.',
    'Correct the amounts until the total matches exactly.',
  ),
  DEPOSIT_ROWS_MISMATCH: t(
    'أقساط وديعة الصيانة لا تساوي الوديعة في ملف العقود.',
    'The maintenanceDeposit rows do not equal the contract’s deposit.',
    'صحح نوع الأقساط أو الوديعة.',
    'Correct the row kinds or the deposit.',
  ),
  DUE_BEFORE_CONTRACT: t(
    'تاريخ الاستحقاق قبل تاريخ العقد (تنبيه فقط).',
    'Due before the contract date (warning only).',
    'تأكد من التاريخ (مقدم حجز مثلًا).',
    'Check the date (a reservation deposit, for example).',
  ),
  CONTRACT_NOT_FOUND: t(
    'العقد غير موجود أو خارج نطاقك.',
    'No such contract in your scope.',
    'استورد العقود وجداولها أولًا.',
    'Import contracts and schedules first.',
  ),
  CONTRACT_NOT_HISTORICAL: t(
    'العقد لم يُستورد كعقد تاريخي.',
    'The contract was not imported as a historical contract.',
    'المقبوضات على العقود الحالية تُسجل من شاشة التحصيل.',
    'Money on current contracts is recorded on the collections screen.',
  ),
  CONTRACT_NOT_ACTIVE: t(
    'العقد غير نشط.',
    'The contract is not active.',
    'راجع حالة العقد.',
    'Check the contract’s state.',
  ),
  RECEIPT_AFTER_CUTOVER: t(
    'تاريخ القبض ليس قبل تاريخ بدء التشغيل.',
    'Received on or after the cutover date.',
    'سجّله من شاشة التحصيل كمقبوض جديد.',
    'Record it on the collections screen as new money.',
  ),
  RECEIVED_BEFORE_CONTRACT: t(
    'تاريخ القبض قبل تاريخ العقد (تنبيه فقط).',
    'Received before the contract date (warning only).',
    'تأكد من التاريخ.',
    'Check the date.',
  ),
  INSTALLMENT_NOT_FOUND: t(
    'لا يوجد قسط بهذا الرقم في العقد.',
    'The contract has no instalment with this sequence.',
    'صحح رقم القسط.',
    'Correct the sequence.',
  ),
  OVER_ALLOCATION: t(
    'المبلغ أكبر من المتبقي على القسط.',
    'The amount is more than the instalment still owes.',
    'صحح المبلغ أو اترك رقم القسط فارغًا.',
    'Correct the amount, or leave the sequence empty.',
  ),
  EXCEEDS_OUTSTANDING: t(
    'المبلغ أكبر من المتبقي على العقد.',
    'The amount is more than the contract still owes.',
    'راجع المقبوضات المكررة أو قيمة العقد.',
    'Check for duplicated receipts or the contract value.',
  ),
  /* ---------------------------------------------------------------- commit */
  IMPORT_PREVIEW_STALE: t(
    'تغيرت البيانات منذ المعاينة.',
    'The data changed since the preview.',
    'أعد التحقق ثم اعتمد.',
    'Validate again, then commit.',
  ),
  COMMIT_FAILED: t(
    'تعذر حفظ السجل.',
    'The record could not be written.',
    'راجع السجل وأعد المحاولة.',
    'Check the record and try again.',
  ),
  DEPENDS_ON_FAILED_ROW: t(
    'تم تخطيه لأن سجلًا سابقًا لنفس العقد فشل.',
    'Skipped because an earlier record of the same contract failed.',
    'صحح السجل الفاشل ثم استورد هذه الصفوف من جديد.',
    'Fix the failed record, then import these rows again.',
  ),
  CONCURRENT_DUPLICATE: t(
    'تم استيراد السجل في نفس اللحظة من عملية أخرى.',
    'Another run imported the record at the same moment.',
    'لا حاجة لأي إجراء.',
    'No action needed.',
  ),
};

export function importIssueText(code: string): ImportIssueText | undefined {
  return IMPORT_ISSUE_TEXT[code];
}
